import { compact, groupBy, intersection, orderBy, uniqBy } from 'lodash';
import { useMemo } from 'react';

import {
  BUSINESS_ENTITY_KINDS,
  CHARITABLE_ENTITY_KINDS,
} from '@/modules/entities/entities.constants';
import { DEATH_PAYOUT_ENTITY_KINDS } from '@/modules/estateWaterfall/waterfallGraph/constants';
import {
  ContextPrimaryClient,
  useHouseholdDetailsContext,
} from '@/modules/household/contexts/householdDetails.context';
import { EntityInEstateStatus, EntityStage } from '@/types/schema';
import { UnreachableError } from '@/utils/errors';

import { EstateWaterfallModal_EntityFragment } from '../../graphql/EstateWaterfallModal.generated';
import { useWaterfallModalQuery } from '../../hooks/useWaterfallModalQuery';
import {
  EntityRow,
  GroupKind,
  GroupRow,
  OwnershipStakeRow,
  Row,
} from '../EntityMultiSelectTable.types';
import {
  getGroupNameForGroupKind,
  isGroupKind,
} from '../EntityMultiSelectTable.utils';

const getGrantorOrOwnerOrDonorIds = (
  entitySubtype: EstateWaterfallModal_EntityFragment['subtype']
): string[] => {
  if ('grantors' in entitySubtype) {
    return compact(entitySubtype.grantors?.map((g) => g.individual?.id));
  }
  if ('owners' in entitySubtype) {
    return compact(entitySubtype.owners?.map((o) => o.individual?.id));
  }
  if ('donors' in entitySubtype) {
    return compact(entitySubtype.donors?.map((d) => d.individual?.id));
  }
  if ('grantor' in entitySubtype) {
    return compact([entitySubtype.grantor?.individual?.id]);
  }
  if ('owner' in entitySubtype) {
    return compact([entitySubtype.owner?.individual?.id]);
  }
  return [];
};

function getGroupKindForEntity(
  entity: EstateWaterfallModal_EntityFragment,
  primaryClients: ContextPrimaryClient[]
) {
  const subtype = entity.subtype;

  if (BUSINESS_ENTITY_KINDS.includes(entity.kind)) {
    throw new UnreachableError({
      case: entity.subtype.__typename as never,
      message: `Business entities should be filtered out ${subtype.__typename}`,
    });
  }

  const primaryClientIds = primaryClients.map((client) => client.id);

  const grantorsOrOwnersOrDonorsIds = getGrantorOrOwnerOrDonorIds(subtype);
  const inEstateStatus =
    ('inEstateStatus' in subtype ? subtype.inEstateStatus : null) ?? null;
  const inEstate = inEstateStatus === EntityInEstateStatus.InEstate;

  const matchingPrimaryClientIds = intersection(
    grantorsOrOwnersOrDonorsIds,
    primaryClientIds
  );

  // Classify by inEstateStatus first — entity type (irrevocable vs revocable)
  // should NOT determine in/out of estate placement.
  if (inEstate) {
    if (matchingPrimaryClientIds.length > 1) return GroupKind.IN_ESTATE_JOINT;
    if (
      matchingPrimaryClientIds.length === 1 &&
      matchingPrimaryClientIds[0] === primaryClientIds[1]
    )
      return GroupKind.IN_ESTATE_GRANTOR_B;
    // Default to grantor A for single-match or no-match-to-primary-client cases
    return GroupKind.IN_ESTATE_GRANTOR_A;
  }

  if (CHARITABLE_ENTITY_KINDS.includes(entity.kind)) {
    return GroupKind.OUT_OF_ESTATE_CHARITY;
  }

  return GroupKind.OUT_OF_ESTATE_FAMILY_GIVING;
}

interface UseEntityMultiSelectTableRowsInput {
  entities: EstateWaterfallModal_EntityFragment[];
  hypotheticalTransfers: ReturnType<
    typeof useWaterfallModalQuery
  >['hypotheticalTransfers'];
}

export function useEntityMultiSelectTableRows({
  entities,
  hypotheticalTransfers,
}: UseEntityMultiSelectTableRowsInput) {
  const { primaryClients } = useHouseholdDetailsContext();

  const rows: Row[] = useMemo(() => {
    const filteredEntities = entities.filter((entity) => {
      if (entity.stage === EntityStage.PreCreated) {
        return false;
      }

      if (DEATH_PAYOUT_ENTITY_KINDS.includes(entity.kind)) {
        return true;
      }

      const entityExists = [EntityStage.Active, EntityStage.Completed].includes(
        entity.stage
      );

      if (!entityExists) {
        // If draft, show entity when it is a transfer source
        return hypotheticalTransfers.some((transfer) => {
          const sourceId = transfer.sourceEntity?.id;
          return sourceId && sourceId === entity.id;
        });
      }

      return (
        entityExists && !BUSINESS_ENTITY_KINDS.includes(entity.kind) // Business entities are represented as individuals
      );
    });

    const entitiesGroupedByEstateStatus = groupBy(filteredEntities, (e) =>
      getGroupKindForEntity(e, primaryClients ?? [])
    );

    // Make group rows for ownership stakes
    const groupRowsForOwnershipStakes: GroupRow[] =
      primaryClients?.flatMap((_client, idx) => {
        const root =
          idx === 0
            ? GroupKind.IN_ESTATE_GRANTOR_A
            : GroupKind.IN_ESTATE_GRANTOR_B;

        return {
          id: root,
          group: root,
          path: [root],
          name: getGroupNameForGroupKind(root, {
            grantorA: primaryClients[0]?.displayGivenName,
            grantorB: primaryClients[1]?.displayGivenName,
          }),
        };
      }) ?? [];

    const groupsForEntities = Object.keys(
      entitiesGroupedByEstateStatus
    ) as GroupKind[];

    // Make the group rows for entities
    const groupRowsForEntities: GroupRow[] = groupsForEntities.map((group) => {
      if (!isGroupKind(group)) {
        throw new UnreachableError({
          case: group,
          message: `Unrecognized group ${group}`,
        });
      }

      return {
        id: group,
        group,
        path: [group],
        name: getGroupNameForGroupKind(group, {
          grantorA: primaryClients?.[0]?.displayGivenName,
          grantorB: primaryClients?.[1]?.displayGivenName,
        }),
      };
    });

    const sortOrder = [
      GroupKind.IN_ESTATE_GRANTOR_A,
      GroupKind.IN_ESTATE_GRANTOR_B,
      GroupKind.IN_ESTATE_JOINT,
      GroupKind.OUT_OF_ESTATE_FAMILY_GIVING,
      GroupKind.OUT_OF_ESTATE_CHARITY,
    ];

    const groupRows = orderBy(
      uniqBy(
        [...groupRowsForOwnershipStakes, ...groupRowsForEntities] as GroupRow[],
        'id'
      ),
      (row) => sortOrder.indexOf(row.group),
      'asc'
    );

    // Make the entity rows
    const entityRows: EntityRow[] = Object.entries(
      entitiesGroupedByEstateStatus
    ).flatMap(([group, entities]) => {
      return entities.flatMap((entity) => ({
        id: entity.id,
        entityId: entity.id,
        path: [group, entity.id],
        name: entity.subtype.displayName,
      }));
    });

    // Make a row for ownership stakes
    const ownershipStakesRows: OwnershipStakeRow[] =
      groupRowsForOwnershipStakes?.flatMap((row) => {
        const root = row.id as
          | GroupKind.IN_ESTATE_GRANTOR_A
          | GroupKind.IN_ESTATE_GRANTOR_B;
        const clientId =
          root === GroupKind.IN_ESTATE_GRANTOR_A
            ? primaryClients?.[0]?.id
            : primaryClients?.[1]?.id;
        const owner = primaryClients?.find((client) => client.id === clientId);

        if (!owner) return [];

        const ownerId = owner.id;

        return {
          id: ownerId,
          individualId: ownerId,
          path: [root, ownerId],
          name: `${owner.displayGivenName}’s directly-held assets`,
        };
      }) ?? [];

    return [...groupRows, ...entityRows, ...ownershipStakesRows];
  }, [entities, hypotheticalTransfers, primaryClients]);

  return {
    rows,
  };
}
